import i18n from '@/i18n';
import { taskAPI, imageFissionAPI, infringementAPI, getIpeWorkspaceMode } from '@/utils/api';
import { allocateTaskIdentifiersLocal } from '@/pages/ipe/workbench/utils/taskIdGenerator';
import {
  createAiStudioImageTask,
} from '@/pages/ipe/workbench/utils/aiStudioImageTaskRecord';
import { createIpeWorkflowTask } from '@/pages/ipe/workbench/utils/ipeWorkflowTaskRecord';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { trackPendingTask } from '@/utils/workbenchHistoryBadge';
import {
  mapStudioTaskReferencePreviews,
  resolveStudioTaskPreviewUrl,
} from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';
import {
  getImageProcessStudioConfig,
  SMART_CUTOUT_DEFAULT_PARAMS,
  normalizeSmartCutoutMode,
  normalizeSmartCutoutFormats,
} from '@/pages/ipe/workbench/constants/imageProcessStudioConfig';
import { TOOL_NAV_ITEMS } from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import { createGoodsTaskFromDetailSources } from '@/pages/ipe/workbench/utils/detailToolBridgeActions';
import { createAiGoodsTaskFromDetailSources } from '@/pages/ipe/workbench/utils/createAiGoodsTaskFromDetailSources';
import { createAndRunAiCopywritingTaskFromDetail } from '@/pages/ipe/workbench/utils/aiCopywritingDetailLaunch';
import { DEFAULT_AI_COPYWRITING_LANGUAGE } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  DEFAULT_MODEL_TRY_ON_GARMENT_TYPE,
  MODEL_STUDIO_NAV_IDS,
} from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { resolveFissionBridgeApiPayload } from '@/pages/ipe/workbench/utils/detailToolBridgeParamsSupport';
import { normalizeImageToolPrefillSource } from '@/pages/ipe/workbench/utils/sourcingToolBridge';

const MODEL_SLOT_BY_NAV = {
  modelTryOn: 'garment',
  modelHandheld: 'product',
  modelPoseFission: 'model',
  modelUniversalWear: 'garment',
};

const MODEL_TASK_META = {
  modelTryOn: { taskName: '模特试衣', type: 'modelTryOn' },
  modelHandheld: { taskName: '手持商品', type: 'modelHandheld' },
  modelPoseFission: { taskName: '姿势裂变', type: 'modelPoseFission' },
  modelUniversalWear: { taskName: '万物穿戴', type: 'modelUniversalWear' },
};

async function resolveSourceUrls(sources) {
  const list = (Array.isArray(sources) ? sources : [])
    .map((s) => normalizeImageToolPrefillSource(s))
    .filter(Boolean);
  if (!list.length) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }
  const imageUrls = [];
  for (const item of list) {
    const resolved = await resolveImg2ImgReferenceForApi(item.url);
    if (!resolved) {
      throw new Error(i18n.t('workbench:ipeUtilError.imageUnresolvedNamed', { name: item.name || item.id }));
    }
    imageUrls.push(resolved);
  }
  return { list, imageUrls };
}

async function createQueuedImageStudioTask({
  navId,
  taskName,
  sources,
  imageUrls,
  initialResult,
  estimatedTotal,
  operatorName = '',
}) {
  const workspaceMode = getIpeWorkspaceMode();
  const { taskId, subAccount } = allocateTaskIdentifiersLocal(navId);
  const imageIds = sources.map((s) => String(s.id)).filter(Boolean);
  const previewUrl = resolveStudioTaskPreviewUrl(sources, imageUrls[0]);
  await createAiStudioImageTask({
    taskType: navId,
    taskName,
    taskId,
    subAccount,
    imageIds,
    previewUrl,
    initialResult: {
      ...initialResult,
      referenceUrls: imageUrls,
      referencePreviews: mapStudioTaskReferencePreviews(sources),
      previewUrl,
      operator: operatorName,
      operatorName,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
    },
    estimatedTotal,
    workspaceMode,
  });
  await taskAPI.submit(taskId);
  return { taskId, navId };
}

async function createImageProcessTask(navId, sources, { studioParams = null, operatorName = '' } = {}) {
  const studioConfig = getImageProcessStudioConfig(navId);
  if (!studioConfig) {
    throw new Error(i18n.t('workbench:ipeUtilError.toolUnsupported', { nav: navId }));
  }
  const { list, imageUrls } = await resolveSourceUrls(sources);
  const estimatedTotal = list.length;
  const sp = studioParams && typeof studioParams === 'object' ? studioParams : {};
  const base = {
    engine: studioConfig.engine,
    jobIds: {},
    localFailedImageIds: [],
  };
  if (navId === 'smartCutout') {
    Object.assign(base, {
      localCutoutUrls: {},
      cutoutMode: normalizeSmartCutoutMode(sp.cutoutMode ?? SMART_CUTOUT_DEFAULT_PARAMS.mode),
      cutoutEngine: 'aliyun',
      cutoutFormats: normalizeSmartCutoutFormats(SMART_CUTOUT_DEFAULT_PARAMS.formats),
      outputFormat: 'png',
    });
  } else if (navId === 'imageEnhance') {
    Object.assign(base, {
      localEnhanceUrls: {},
      engine: 'qwen-image-2.0',
      pipeline: 'qwen-clarity-v1',
    });
  } else if (navId === 'stampExtract') {
    Object.assign(base, { localStampUrls: {} });
  }
  return createQueuedImageStudioTask({
    navId,
    taskName: studioConfig.taskName,
    sources: list,
    imageUrls,
    initialResult: base,
    estimatedTotal,
    operatorName,
  });
}

async function createAiOutpaintTask(sources, { studioParams = null, operatorName = '' } = {}) {
  const { list, imageUrls } = await resolveSourceUrls(sources);
  const sp = studioParams && typeof studioParams === 'object' ? studioParams : {};
  const expandParams = {
    expandMode: sp.expandMode ?? 1,
    scale: sp.scale,
    scaleCustom: sp.scaleCustom,
    aspectRatio: sp.aspectRatio,
    outputWidth: sp.outputWidth,
    outputHeight: sp.outputHeight,
  };
  return createQueuedImageStudioTask({
    navId: 'aiOutpaint',
    taskName: 'AI扩图',
    sources: list,
    imageUrls,
    initialResult: {
      expandParams,
      generateCount: 1,
      processedSourceIndices: [],
    },
    estimatedTotal: list.length,
    operatorName,
  });
}

async function createImg2ImgTask(sources, { prompt = '', operatorName = '' } = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const { list, imageUrls } = await resolveSourceUrls(sources);
  // 一图一生
  const qty = 1;
  const { taskId, subAccount } = allocateTaskIdentifiersLocal('img2img');
  const imageIds = list.map((s) => String(s.id));
  const totalUnits = Math.max(1, imageUrls.length * qty);
  await createIpeWorkflowTask({
    id: taskId,
    type: 'img2img',
    name: '万能改图任务',
    title: `万能改图 ${subAccount}`,
    subAccount,
    imageIds,
    previewUrl: resolveStudioTaskPreviewUrl(list, imageUrls[0]),
    status: 'processing',
    total: totalUnits,
    successCount: 0,
    failedCount: 0,
    result: {
      prompt: String(prompt || '').trim(),
      referenceUrls: imageUrls,
      referencePreviews: mapStudioTaskReferencePreviews(list),
      generateCount: qty,
      batchMode: imageUrls.length > 1,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
      generationMode: 'img2img',
      operator: operatorName,
      operatorName,
    },
  });
  trackPendingTask('img2img', taskId, workspaceMode);
  await taskAPI.submit(taskId);
  return { taskId, navId: 'img2img' };
}

async function createAiTextToImageTask(sources, { prompt = '', operatorName = '' } = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const apiPrompt = String(prompt || '').trim()
    || (Array.isArray(sources) && sources[0]?.name ? String(sources[0].name).trim() : '')
    || '商品图';
  const qty = 1;
  const { taskId, subAccount } = allocateTaskIdentifiersLocal('aiTextToImage');
  await createAiStudioImageTask({
    taskType: 'aiTextToImage',
    taskName: '文生图',
    taskId,
    subAccount,
    imageIds: [],
    previewUrl: '',
    initialResult: {
      prompt: apiPrompt,
      generateCount: qty,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
      operator: operatorName,
      operatorName,
    },
    estimatedTotal: qty,
    workspaceMode,
  });
  await taskAPI.submit(taskId);
  return { taskId, navId: 'aiTextToImage' };
}

async function createFissionTask(sources, { billingNavId = 'imageFission', studioParams = null } = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const { list, imageUrls } = await resolveSourceUrls(sources);
  const fissionSources = list.map((item, index) => ({
    id: item.id,
    name: item.name,
    url: imageUrls[index],
    previewUrl: item.previewUrl || imageUrls[index],
  }));
  const apiPayload = resolveFissionBridgeApiPayload({
    ...(studioParams && typeof studioParams === 'object' ? studioParams : {}),
    patternOnly: billingNavId === 'patternFission'
      ? true
      : (studioParams?.patternOnly === true),
  });
  const res = await imageFissionAPI.createBatch({
    sources: fissionSources,
    saveToMaterial: true,
    billingNavId,
    ...apiPayload,
  });
  const batchId = res?.id || res?.batchId;
  if (!batchId) throw new Error(i18n.t('workbench:ipeUtilError.batchIdMissing'));
  trackPendingTask('imageFission', batchId, workspaceMode);
  return { taskId: batchId, navId: billingNavId, batch: res };
}

async function createModelStudioTask(navId, sources, {
  prompt = '',
  modelStudioParams = null,
  operatorName = '',
} = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const meta = MODEL_TASK_META[navId];
  if (!meta) throw new Error(i18n.t('workbench:ipeUtilError.toolUnsupportedModel', { nav: navId }));
  const { list, imageUrls } = await resolveSourceUrls(sources);
  const slotKey = MODEL_SLOT_BY_NAV[navId] || 'garment';
  const generateCount = Math.max(1, Number(modelStudioParams?.generateCount) || 1);
  const estimatedTotal = Math.max(1, list.length * generateCount);
  const { taskId, subAccount } = allocateTaskIdentifiersLocal(navId);
  const imageIds = list.map((s) => String(s.id));
  const previews = mapStudioTaskReferencePreviews(list);
  const extra = modelStudioParams && typeof modelStudioParams === 'object' ? modelStudioParams : {};
  const urlField = slotKey === 'model'
    ? { modelImageUrls: imageUrls, modelImageUrl: imageUrls[0], modelCount: list.length }
    : slotKey === 'product'
      ? { productImageUrls: imageUrls, productCount: list.length }
      : { garmentImageUrls: imageUrls, garmentCount: list.length };

  await createAiStudioImageTask({
    taskType: navId,
    taskName: meta.taskName,
    taskId,
    subAccount,
    imageIds,
    previewUrl: previews[0] || '',
    initialResult: {
      prompt: String(extra.prompt || prompt || '').trim(),
      userPrompt: String(extra.prompt || prompt || '').trim(),
      generateCount,
      modelType: extra.modelType || 'female',
      modelEthnicity: extra.modelEthnicity || '',
      garmentType: extra.garmentType || DEFAULT_MODEL_TRY_ON_GARMENT_TYPE,
      frameScope: extra.frameScope || 'fullBody',
      batchSelectEnabled: true,
      selectedBatchCount: list.length,
      referencePreviews: previews,
      previewUrl: previews[0] || '',
      operator: operatorName,
      operatorName,
      serverWorker: true,
      batchPhase: 'queued',
      outputs: [],
      urls: [],
      ...urlField,
    },
    estimatedTotal,
    workspaceMode,
  });
  trackPendingTask(navId, taskId, workspaceMode);
  await taskAPI.submit(taskId);
  return { taskId, navId };
}

async function createInfringementTask(sources, { studioParams = null, operatorName = '' } = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const { imageUrls } = await resolveSourceUrls(sources);
  const sp = studioParams && typeof studioParams === 'object' ? studioParams : {};
  const mode = sp.filterMode === 'deep' ? 'deep' : 'basic';
  const created = await infringementAPI.createBatch({
    imageUrls,
    mode,
    operatorName,
  });
  const taskId = created?.id;
  if (taskId == null) throw new Error(i18n.t('workbench:ipeUtilError.infringementCreateFailed'));
  trackPendingTask('infringementDetection', taskId, workspaceMode);
  return { taskId, navId: 'infringementDetection' };
}

async function createImageCropTask(sources, { operatorName = '' } = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const { list, imageUrls } = await resolveSourceUrls(sources);
  const { taskId, subAccount } = allocateTaskIdentifiersLocal('imageCrop');
  const imageIds = list.map((s) => String(s.id));
  const urlById = new Map(list.map((item, index) => [String(item.id), imageUrls[index]]));
  const initialResult = {
    referencePreviews: mapStudioTaskReferencePreviews(list),
    previewUrl: resolveStudioTaskPreviewUrl(list, imageUrls[0]),
    operator: operatorName,
    operatorName,
    engine: 'qwen-detect',
    referenceUrls: imageUrls,
  };
  await createAiStudioImageTask({
    taskType: 'imageCrop',
    taskName: '图案裁剪',
    taskId,
    subAccount,
    imageIds,
    previewUrl: initialResult.previewUrl,
    initialResult,
    estimatedTotal: list.length,
    workspaceMode,
  });

  const { ensureImageCropTaskInBackground } = await import('@/image-crop');
  ensureImageCropTaskInBackground(
    taskId,
    imageIds,
    (id) => urlById.get(String(id)) || null,
    { subAccount, taskMeta: { id: taskId, subAccount, taskId } },
  );

  return { taskId, navId: 'imageCrop' };
}

/**
 * 详情页快捷功能确认：在对应功能创建任务（默认参数）并返回 taskId。
 * @returns {Promise<{ taskId: string, navId: string, batch?: object }>}
 */
export async function createTaskFromDetailToolBridge({
  navId,
  sources = [],
  prompt = '',
  language = '',
  studioParams = null,
  modelStudioParams = null,
  podSelectedTemplates = null,
  fromCropTaskId = '',
  sourceGoodsTaskId = '',
  operatorName = '',
} = {}) {
  const id = String(navId || '').trim();
  if (!id) throw new Error(i18n.t('workbench:ipeUtilError.targetToolMissing'));

  if (id === 'goods') {
    const created = await createAiGoodsTaskFromDetailSources({
      sources,
      studioParams,
      operatorName,
      fromCropTaskId,
      sourceGoodsTaskId,
    });
    return { taskId: created.id, navId: 'goods' };
  }

  if (id === 'podProductDesign') {
    const templates = Array.isArray(podSelectedTemplates) ? podSelectedTemplates : [];
    if (!templates.length) {
      throw new Error(i18n.t('workbench:ipeUtilError.selectTemplateSetFirst'));
    }
    const created = await createGoodsTaskFromDetailSources({
      sources,
      templates,
      billingNavId: 'podProductDesign',
      fromCropTaskId,
    });
    return { taskId: created.id, navId: 'podProductDesign' };
  }

  if (id === 'aiCopywriting') {
    const uploadMode = String(studioParams?.uploadMode || '').trim().toLowerCase() === 'custom'
      ? 'custom'
      : 'default';
    const created = await createAndRunAiCopywritingTaskFromDetail({
      sources,
      prompt,
      language: language || studioParams?.language || DEFAULT_AI_COPYWRITING_LANGUAGE,
      maxChars: studioParams?.maxChars,
      uploadMode,
      titlePresetId: studioParams?.titlePresetId || '',
      titlePresetName: studioParams?.titlePresetName || '',
      titlePresetContent: studioParams?.titlePresetContent || '',
      sourceGoodsTaskId,
      operatorName,
    });
    return { taskId: created.taskId, navId: 'aiCopywriting' };
  }

  if (id === 'smartCutout' || id === 'imageEnhance' || id === 'stampExtract') {
    return createImageProcessTask(id, sources, { studioParams, operatorName });
  }
  if (id === 'aiOutpaint') {
    return createAiOutpaintTask(sources, { studioParams, operatorName });
  }
  if (id === 'img2img') {
    const promptText = String(studioParams?.prompt ?? prompt ?? '').trim();
    return createImg2ImgTask(sources, { prompt: promptText, operatorName });
  }
  if (id === 'aiTextToImage') {
    return createAiTextToImageTask(sources, { prompt, operatorName });
  }
  if (id === 'imageFission' || id === 'patternFission') {
    return createFissionTask(sources, { billingNavId: id, studioParams });
  }
  if (MODEL_STUDIO_NAV_IDS.includes(id)) {
    const params = studioParams && typeof studioParams === 'object'
      ? studioParams
      : (modelStudioParams && typeof modelStudioParams === 'object' ? modelStudioParams : {});
    return createModelStudioTask(id, sources, {
      prompt: String(params.prompt || prompt || '').trim(),
      modelStudioParams: params,
      operatorName,
    });
  }
  if (id === 'infringementDetection') {
    return createInfringementTask(sources, { studioParams, operatorName });
  }
  if (id === 'imageCrop') {
    return createImageCropTask(sources, { operatorName });
  }

  const label = TOOL_NAV_ITEMS[id]?.label || id;
  throw new Error(i18n.t('workbench:ipeUtilError.toolUnsupportedFromDetail', { label }));
}
